import cn from "classnames"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { Button, Checkbox, DatePicker as DatePickerAntd } from "@/atoms"
import { getDate, getDateInFormat } from "@/helpers/date.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import {
  DATE_FORMAT_ISO_FORMAT,
  DATE_TIME_LT_FORMAT,
  RANGE_DELIMITER,
  TIME_FORMAT
} from "@/organisms/SmartSearch/const/date.constants"

import styles from "./DatePicker.module.scss"

import type { Dayjs } from "@/helpers/date.helpers.types"
import type { DatePickerProps, State } from "./DatePicker.types"

export const DatePicker = (props: DatePickerProps) => {
  const [value, setValue] = useState<State>([null, null])
  const container = useRef<HTMLDivElement | null>(null)
  const [showTime, setShowTime] = useState(false)
  const defaultValue = useRef<State | null>(null)
  const { t } = useTranslation("translation", {
    keyPrefix: "filters.datePicker.preset"
  })

  useEffect(() => {
    if (!props.open) return

    const parent = props.refInput.current?.querySelector(".edit-smart-search")
    const el = parent?.querySelector(`[data-time-range]`)

    if (!el) return setValue([null, null])

    const dateTimeRange = el.getAttribute("data-time-range")
    const isEmpty = dateTimeRange === "true"
    const isExist = dateTimeRange !== "false"

    if (isEmpty || isExist) {
      if (isExist && !isEmpty) {
        props.handlers.updateTokenValue("")
        el.setAttribute("data-time-range", "true")
      }

      container.current = el as HTMLDivElement
    }

    if (isExist && !isEmpty && dateTimeRange) {
      const nextValue = dateTimeRange
        .split(RANGE_DELIMITER)
        .map((d) => getDate(d)) as State

      defaultValue.current = nextValue
      setValue(nextValue)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.html, props.services.StringService.getCursorPosition()])

  if (!container.current) return null

  const startDate =
    value[0] && getDateInFormat(value[0], DATE_FORMAT_ISO_FORMAT)
  const endDate = value[1] && getDateInFormat(value[1], DATE_FORMAT_ISO_FORMAT)

  const onChange = (_: [Dayjs | null, Dayjs | null]) => {
    defaultValue.current = null

    props.onChange(
      `"${_?.map((d, index) => {
        if (!showTime && index === 1) return d?.endOf("d")

        return d
      })
        .map((d) => d?.format(TIME_FORMAT))
        .join(RANGE_DELIMITER)}"`,
      true
    )
  }

  const endOfToday = getDate().endOf("d")

  return createPortal(
    <DatePickerAntd.RangePicker
      autoFocus
      defaultOpen
      rootClassName={styles.root}
      components={{
        button: ({ children, ...rest }) => {
          const range = props.refInput.current
            ?.querySelector(".smart-search-time-picker-input-active")
            ?.querySelectorAll("input")[0]
            ?.getAttribute("date-range")

          return (
            <Button size="small" type="primary" {...rest}>
              {range === "start" ? "Next" : "Ok"}
            </Button>
          )
        }
      }}
      cellRender={(current, info) => {
        if (info.type !== "date") return info.originNode

        if (typeof current === "number" || typeof current === "string") {
          return (
            <div className="smart-search-time-picker-cell-inner">{current}</div>
          )
        }

        const currentDate = current.format(DATE_FORMAT_ISO_FORMAT)
        const isSelected = startDate === currentDate || endDate === currentDate

        return (
          <div
            className={cn(
              "smart-search-time-picker-cell-inner",
              isSelected && styles.selected
            )}
          >
            {current.date()}
          </div>
        )
      }}
      onCalendarChange={(a, _, c) => {
        defaultValue.current = a

        if (c.range === "start") setValue((prev) => [a[0], prev[1]])
        else if (c.range === "end") setValue((prev) => [prev[0], a[1]])
      }}
      showTime={showTime ? { format: "HH:mm" } : showTime}
      renderExtraFooter={() => (
        <Checkbox checked={showTime} onChange={() => setShowTime(!showTime)}>
          Include time
        </Checkbox>
      )}
      presets={[
        { label: t("24h"), value: [getDate().subtract(24, "h"), getDate()] },
        {
          label: t("days", { days: 7 }),
          value: [getDate().subtract(7, "d").startOf("d"), endOfToday]
        },
        {
          label: t("days", { days: 14 }),
          value: [getDate().subtract(14, "d").startOf("d"), endOfToday]
        },
        {
          label: t("days", { days: 30 }),
          value: [getDate().subtract(1, "M").startOf("d"), endOfToday]
        }
      ]}
      prefixCls="smart-search-time-picker"
      variant="borderless"
      size="small"
      value={value}
      format={DATE_TIME_LT_FORMAT}
      minDate={getDate("2020-01-01")}
      maxDate={getDate()}
      preserveInvalidOnBlur
      onChange={(_, dates) => {
        if (dates?.[0] && dates?.[1] && _) onChange(_)
      }}
      onOpenChange={(open) => {
        if (!defaultValue.current) return

        const [start, end] = defaultValue.current

        if (!open && start && end) onChange([start, end])
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
    />,
    container.current
  )
}
